import React from 'react';
import { Tooltip } from '../components/ui/Tooltip';
import { config } from '../config/config';
import { useViewedPros } from '../contexts/ViewedProsContext';
import { proDetailPath } from './proSearch';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type GroupByOption = 'none' | 'customer' | 'origin' | 'destination';
export type SortField = 'pronumber' | 'customerName' | 'originName' | 'destinationName' | 'sizeType' | 'isLiveLoad' | null;
export type SortDirection = 'asc' | 'desc';
export type CardHeight = 'sm' | 'md' | 'lg';

// ============================================================================
// LEG TYPE UTILITIES
// ============================================================================

/**
 * Abbreviates leg type names for compact table display
 * Examples: PICKUP -> PU, DELIVERY -> D, TERMINATE -> T
 */
export const abbreviateLegType = (legType: string | undefined | null): string => {
  if (!legType) return '-';

  const abbreviations: { [key: string]: string } = {
    'BOBTAIL': 'BT',
    'CHASSIS SPLIT': 'CS',
    'DELIVERY': 'D',
    'GROUNDED': 'GR',
    'PICKUP': 'PU',
    'REJECTED': 'REJ',
    'REWORK': 'REW',
    'SCALE': 'SC',
    'STOPOFF': 'SO',
    'STORAGE': 'STG',
    'TERMINATE': 'T',
    'TRANSLOAD': 'TRA',
    'ADD PRODUCT': 'ADD',
    'REMOVE PRODUCT': 'REM',
    'IMPORT': 'I',
    'EXPORT': 'E',
    'RAIL TO PORT': 'R',
    'VAN': 'V',
    'INBOUND': 'IN',
    'OUTBOUND': 'OUT',
    'DOMESTIC': 'DO',
    'REPO': 'RE'
  };

  return abbreviations[legType.toUpperCase()] || legType;
};

// ============================================================================
// TEXT UTILITIES
// ============================================================================

/**
 * Abbreviates customer names for display
 */
export const abbreviateCustomerName = (name: string | undefined): string => {
  if (!name) return '';
  return name.replace(/International/gi, 'INT.');
};

// ============================================================================
// UI COMPONENTS
// ============================================================================

/**
 * Component to render text with tooltip (truncates based on column width)
 */
export const TruncatedWithTooltip: React.FC<{ text: string | null | undefined }> = ({ text }) => {
  const displayText = text || '';

  // Replace newlines with spaces for tooltip display
  const tooltipText = displayText.replace(/\n/g, ' ');

  return (
    <Tooltip content={tooltipText} placement="top">
      <div className="truncate cursor-help">
        {displayText}
      </div>
    </Tooltip>
  );
};

/**
 * Renders Pro# as a clickable link.
 *
 * `target` controls the destination:
 *   - `'cms-v1'` (default) — opens legacy CMS V1 in a new tab. Existing behavior.
 *   - `'search'` — opens the gwi360 search results page in a new tab. Native anchor
 *     so middle-click / cmd-click / right-click open-in-new-tab work as expected.
 *
 * Defaults to `'cms-v1'` for backwards compatibility — call sites opt in to
 * `'search'` as they migrate off v1. (There was a third `'pro-page'` target that
 * deep-linked `/pro/:pronumber`; every pro link now goes through search results,
 * so it was dropped rather than left as an unused second destination.)
 */
export const ProNumberLink: React.FC<{
  pronumber: number | string;
  target?: 'cms-v1' | 'search';
}> = ({ pronumber, target = 'cms-v1' }) => {
  const { isViewed, markViewed } = useViewedPros();
  const viewed = isViewed(pronumber);
  const baseLinkClass = 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer transition-colors duration-150';
  const viewedClass = viewed ? 'bg-green-200 dark:bg-green-600/50 px-1 rounded' : '';
  const linkClassName = `${baseLinkClass} ${viewedClass}`;
  const viewedTitle = viewed ? 'Viewed' : undefined;

  if (target === 'search') {
    return (
      <a
        href={proDetailPath(pronumber)}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClassName}
        title={viewedTitle}
        onClick={() => markViewed(pronumber)}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {pronumber}
      </a>
    );
  }

  const legacyCmsUrl = config.LEGACY_CMS_URL;
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
    markViewed(pronumber);
    const url = `${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <a
      href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClassName}
      title={viewedTitle}
      onClick={handleClick}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {pronumber}
    </a>
  );
};

// ============================================================================
// ORDER KEY UTILITIES
// ============================================================================

/**
 * Generates a unique key for an order row
 */
export const getOrderKey = (order: { pronumber: number; legnum?: number }): string => {
  return `${order.pronumber}-${order.legnum}`;
};

/**
 * Generates a hover key for multileg orders (use just PRO# so all legs share same hover)
 */
export const getHoverKey = (order: { pronumber: number; isMultiStop?: boolean; legnum?: number }): string => {
  return order.isMultiStop ? String(order.pronumber) : getOrderKey(order);
};

/**
 * Gets all multi-stop order keys from a list
 */
export const getMultiStopKeys = (orderList: { pronumber: number; legnum?: number; isMultiStop?: boolean }[]): string[] => {
  return orderList
    .filter(order => order.isMultiStop)
    .map(order => getOrderKey(order));
};

// ============================================================================
// CSS UTILITIES
// ============================================================================

/**
 * Get col-span class based on span value
 */
export const getColSpanClass = (span: number): string => {
  switch (span) {
    case 1:
      return 'lg:col-span-1';
    case 2:
      return 'lg:col-span-2';
    case 3:
      return 'lg:col-span-3';
    case 4:
      return 'lg:col-span-4';
    default:
      return 'lg:col-span-1';
  }
};

/**
 * Get grid column start class based on column number
 */
export const getGridColumnStartClass = (column: number): string => {
  switch (column) {
    case 0:
      return 'lg:col-start-1';
    case 1:
      return 'lg:col-start-2';
    case 2:
      return 'lg:col-start-3';
    case 3:
      return 'lg:col-start-4';
    default:
      return 'lg:col-start-1';
  }
};
